/** The review bar's top, in px from the viewport's bottom, which bottom-anchored toasts clear (notifications.css). */
export const REVIEW_BAR_OFFSET_VAR = "--review-bar-offset";

/** How long the bar takes to rise in or drop away (index.css `--animate-review-bar-*`). */
export const REVIEW_BAR_TRANSITION_MS = 150;

/**
 * The bar's bottom, in px from the viewport's bottom: 1rem above the footer row (Back / Complete), and 2rem up
 * on a page with none.
 */
export const barBottomPx = (footerHeight: number, rem: number): number => Math.max(2 * rem, footerHeight + rem);

/** The bar keeps at least this much of the viewport clear on its left. */
export const VIEWPORT_MARGIN_PX = 16;

/** The card stack's slots inset their cards this much from the column (StackSlot's px-3). */
export const CARD_INSET_PX = 12;

/**
 * How much more room than a roomier layout needs before the bar steps back to it, so a width near the edge
 * can't flip it back and forth.
 */
export const COMPACT_HYSTERESIS_PX = 24;

/**
 * A last resort, only when the bar growing left from the cards' right edge would cross the viewport's left
 * margin: 0 is the full layout, 1 truncates the picked view's name, 2 also shrinks Quality Review's status to
 * its icon. Resolve always keeps its label.
 */
export type SqueezeLevel = 0 | 1 | 2;
export const TIGHTEST_SQUEEZE: SqueezeLevel = 2;

/**
 * The next level from the width measured at each level so far: one step tighter while the current layout
 * overflows, one step roomier once the roomier layout fits with the hysteresis margin to spare.
 */
export const nextSqueezeLevel = (
  level: SqueezeLevel,
  widths: Partial<Record<SqueezeLevel, number>>,
  maxWidth: number | undefined,
): SqueezeLevel => {
  if (maxWidth === undefined) return level;
  const current = widths[level];
  if (current !== undefined && current > maxWidth && level < TIGHTEST_SQUEEZE) return (level + 1) as SqueezeLevel;
  const roomier = level > 0 ? widths[(level - 1) as SqueezeLevel] : undefined;
  if (roomier !== undefined && roomier + COMPACT_HYSTERESIS_PX <= maxWidth) return (level - 1) as SqueezeLevel;
  return level;
};
